<template>
    <div
        :pendo-analytics="pendoAnalyticsTag"
        class="import-feedback-source-card"
        :class="{
            'import-feedback-source-card--disabled': cardDisabled,
            'import-feedback-source-card--clickable': !cardDisabled,
        }"
        @click="handleClick">
        <img
            :class="{ 'import-feedback-source-card__icon--disabled': cardDisabled }"
            :src="sourceConfig.icon"
            height="32px"
            width="32px">
        <h4 class="import-feedback-source-card__title">
            {{ sourceConfig.title }}
        </h4>
        <p class="import-feedback-source-card__description">
            {{ sourceConfig.description }}
        </p>
        <pendo-button
            class="import-feedback-source-card__button"
            :class="{
                'import-feedback-source-card__button--upgrade': noProductLinePurchased,
            }"
            :label="buttonText"
            :prefix-icon="noProductLinePurchased ? 'arrow-up-circle' : undefined"
            theme="app"
            size="mini"
            type="secondary" />
        <div
            v-if="!sourceConfig.isAvailable"
            class="import-feedback-source-card__coming-soon">
            {{ $t('vocImportFeedbackDrawer.sourceCard.comingSoon') }}
        </div>
        <pendo-tag
            v-else-if="!cardDisabled && sourceConfig.isBeta"
            class="import-feedback-source-card__beta-pill"
            type="beta"
            size="small" />
        <pendo-modal
            :visible="showExploreRequiredModal"
            :title="$t('vocImportFeedbackDrawer.sourceCard.exploreRequiredModal.title')"
            height="auto"
            width="514px"
            @close="showExploreRequiredModal = false">
            <p>
                {{ $t('vocImportFeedbackDrawer.sourceCard.exploreRequiredModal.description') }}
                <br>
                <br>
                {{ $t('vocImportFeedbackDrawer.sourceCard.exploreRequiredModal.forMoreInfo') }}
                <a
                    href="https://support.pendo.io/hc/en-us/articles/37643157438107-Enable-Listen-Explore"
                    target="_blank">{{ $t('vocImportFeedbackDrawer.sourceCard.exploreRequiredModal.learnMore') }}</a>
            </p>
            <template #footer>
                <pendo-button
                    :label="$t('vocImportFeedbackDrawer.sourceCard.exploreRequiredModal.setupButton')"
                    theme="app"
                    type="primary"
                    @click="handleGoToExploreSetup" />
            </template>
        </pendo-modal>
    </div>
</template>

<script>
import { PendoButton, PendoTag, PendoModal } from '@pendo/components';

export default {
    name: 'ImportFeedbackSourceCard',
    components: {
        PendoButton,
        PendoTag,
        PendoModal,
    },
    props: {
        sourceConfig: {
            type: Object,
            required: true,
        },
    },
    emits: ['close'],
    data () {
        return {
            showExploreRequiredModal: false,
        };
    },
    computed: {
        cardDisabled () {
            return !this.isSourceAvailableForUser;
        },
        isSourceAvailableForUser () {
            return (
                this.sourceConfig.isAvailable &&
                !this.noProductLinePurchased &&
                this.sourceConfig.hasPermission
            );
        },
        buttonText () {
            if (!this.sourceConfig.isAvailable) {
                return this.$t('vocImportFeedbackDrawer.sourceCard.button.notifyMeWhenAvailable');
            }
            if (this.noProductLinePurchased) {
                return this.$t('vocImportFeedbackDrawer.sourceCard.button.upgrade');
            }
            if (!this.sourceConfig.hasPermission) {
                return this.$t('vocImportFeedbackDrawer.sourceCard.button.requestPermission');
            }

            return this.sourceConfig.buttonLabel;
        },
        noProductLinePurchased () {
            return this.sourceConfig.productLineCheck === false;
        },
        pendoAnalyticsTag () {
            if (!this.sourceConfig.isAvailable) {
                return `connect-${this.sourceConfig.id}--coming-soon`;
            }
            if (this.noProductLinePurchased) {
                return `connect-${this.sourceConfig.id}--upgrade`;
            }
            if (!this.sourceConfig.hasPermission) {
                return `connect-${this.sourceConfig.id}--no-permission`;
            }
            if (this.sourceConfig.isBeta) {
                return `connect-${this.sourceConfig.id}--beta`;
            }

            return `connect-${this.sourceConfig.id}`;
        },
    },
    methods: {
        handleClick () {
            if (!this.sourceConfig.isAvailable) {
                return;
            }
            if (this.noProductLinePurchased) {
                if (this.sourceConfig.upgradeAction) {
                    this.sourceConfig.upgradeAction();
                }
            } else if (!this.sourceConfig.hasPermission) {
                if (this.sourceConfig.noPermissionAction) {
                    this.sourceConfig.noPermissionAction();
                }
            } else if (this.sourceConfig.requiresExploreSetup) {
                this.showExploreRequiredModal = true;
            } else if (this.sourceConfig.onClick) {
                this.sourceConfig.onClick();
            }
        },
        handleGoToExploreSetup () {
            this.showExploreRequiredModal = false;
            this.$emit('close');
            this.$router.push({ name: 'ListenFeedbackExplore' });
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/voc/styles/voc.style.less';
.import-feedback-source-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    border: 1px solid var(--palette-gray-40);
    border-radius: 3px;
    gap: 8px;
    padding: 16px;
    background: var(--palette-gray-0);
    width: 100%;
    position: relative;
    &__icon {
        &--disabled {
            opacity: 0.5;
        }
    }
    &--disabled {
        background: var(--palette-gray-20);
    }
    &--clickable {
        cursor: pointer;
    }
    &__title {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
    }
    &__description {
        font-size: 14px;
        margin: 0;
        padding: 0;
    }
    &__button {
        margin-top: auto;
        &--upgrade {
            color: var(--palette-pink-100);
            border-color: var(--palette-pink-100);
            &:hover {
                color: var(--palette-pink-100);
                border-color: var(--palette-pink-100);
                background: var(--palette-gray-0);
            }
        }
    }
    &__coming-soon {
        position: absolute;
        right: 16px;
        border-radius: 3px;
        background: var(--palette-gray-0);
        padding: 4px 6px;
        border: 1px solid var(--palette-gray-40);
    }
    &__beta-pill {
        position: absolute;
        right: 16px;
    }
}
</style>
